import React, { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Trash2, Sparkles, Send } from "lucide-react";

export default function AdminReleaseNotes() {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState("");
  const [itemsText, setItemsText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/release-notes").then((r) => setNotes(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const publish = async () => {
    const items = itemsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!title.trim() || items.length === 0) return toast.error("Inserisci titolo e almeno una novità (una per riga)");
    setSaving(true);
    try {
      await api.post("/admin/release-notes", { title: title.trim(), items });
      toast.success("Nota pubblicata — i clienti la vedranno con il prossimo aggiornamento");
      setTitle("");
      setItemsText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pubblicazione");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa nota di rilascio?")) return;
    try {
      await api.delete(`/admin/release-notes/${id}`);
      toast.success("Nota eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Comunicazioni</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
        <Megaphone className="w-8 h-8" /> Note di rilascio
      </h1>
      <p className="text-velta-mute mb-10">
        Quando pubblichi un'implementazione, scrivi qui cosa è cambiato: il cliente vedrà le novità nel banner
        di aggiornamento sul tablet e in un popup dopo l'aggiornamento.
      </p>

      <div className="bg-white border border-velta-border rounded-md p-6 mb-8">
        <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#2D5594]" /> Nuova nota
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Titolo aggiornamento</label>
            <input
              data-testid="relnote-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Aggiornamento di giugno — Stampa etichette"
              className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Novità (una per riga)</label>
            <textarea
              data-testid="relnote-items"
              rows={5}
              value={itemsText}
              onChange={(e) => setItemsText(e.target.value)}
              placeholder={"Aggiunta stampa etichette prezzi dal magazzino\nVelocizzata la ricerca prodotti in cassa\nCorretto arrotondamento sconti"}
              className="w-full p-3 border border-velta-border rounded-md text-sm outline-none focus:border-velta-ink resize-none"
            />
          </div>
          <button
            data-testid="relnote-publish"
            onClick={publish}
            disabled={saving}
            className="h-11 px-6 bg-velta-ink text-white rounded-md font-medium hover:bg-[#162244] inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> {saving ? "Pubblicazione…" : "Pubblica nota"}
          </button>
        </div>
      </div>

      <h3 className="font-heading text-xl font-semibold mb-4">Note pubblicate ({notes.length})</h3>
      <div className="space-y-3">
        {notes.length === 0 && <p className="text-sm text-velta-mute" data-testid="relnote-empty">Nessuna nota pubblicata.</p>}
        {(Array.isArray(notes) ? notes : []).map((n) => (
          <div key={n.id} data-testid={`relnote-card-${n.id}`} className="bg-white border border-velta-border rounded-md p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-heading font-semibold text-lg">{n.title}</div>
                <div className="text-[11px] font-mono text-velta-mute">{formatDate(n.created_at)}</div>
              </div>
              <button
                data-testid={`relnote-del-${n.id}`}
                onClick={() => del(n.id)}
                title="Elimina"
                className="h-8 w-8 rounded hover:bg-red-50 flex items-center justify-center text-velta-error shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <ul className="mt-3 space-y-1.5">
              {(Array.isArray(n.items) ? n.items : []).map((it, i) => (
                <li key={i} className="text-sm flex gap-2">
                  <span className="text-[#2D5594] font-bold">•</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
